import {
  MCP_OAUTH_AUTHORIZE_TOOL_NAME,
  McpOAuthAuthorizeInputSchema,
  McpOAuthAuthorizeOutputSchema,
} from '@terra/shared-dto-chat-types';
import {
  getMcpApiErrorMessage,
  waitForMcpOAuthPopupCallback,
} from '@terra/shared-contracts-mcp-servers';
import { openMcpConnectionSettings } from './open-mcp-connection-settings';
import { mcpServersClient } from '@terra/shared-rest-clients-mcp-servers';
import { terraProxyApiClient } from '../../../../clients/testerClient';
import { getCurrentGatewayUrl } from '../../../../utils/environmentDetection';
import { buildMcpOAuthAuthorizeQuery } from '../../../../utils/mcpOAuthAuthorizeQuery';
import { getAuthHeaders } from '../../../../utils/authHeaders';
import {
  parsePortalToolHandlerInput,
  isElectronApiAvailable,
  getElectronApiUnavailableResult,
} from '../portal-tool-handler.utils';
import type { PortalToolHandler } from '../types';

const buildClientOptions = () => ({
  baseUrl: `${getCurrentGatewayUrl()}/ai`,
});

const fetchAuthorizeUrl = async ({
  mcpServerId,
  identityId,
}: {
  mcpServerId: string;
  identityId?: string;
}): Promise<string> => {
  const authHeaders = await getAuthHeaders();
  const query = await buildMcpOAuthAuthorizeQuery();
  const response = identityId
    ? await mcpServersClient.oauthAuthorizeIdentity({
        params: { id: mcpServerId, identityId },
        query,
        overrideClientOptions: buildClientOptions(),
        extraHeaders: authHeaders,
      })
    : await mcpServersClient.oauthAuthorize({
        params: { id: mcpServerId },
        query,
        overrideClientOptions: buildClientOptions(),
        extraHeaders: authHeaders,
      });
  if (response.status !== 200) {
    throw new Error(
      getMcpApiErrorMessage(
        response.body,
        identityId
          ? 'Failed to authorize MCP identity'
          : 'Failed to authorize MCP server',
      ),
    );
  }
  return response.body.authorizeUrl;
};

const completeOAuthCallback = async ({
  code,
  state,
}: {
  code: string;
  state: string;
}): Promise<void> => {
  const authHeaders = await getAuthHeaders();
  const callbackUrl = new URL(
    `${getCurrentGatewayUrl().replace(/\/$/, '')}/ai/mcp-servers/oauth/callback`,
  );
  callbackUrl.searchParams.set('code', code);
  callbackUrl.searchParams.set('state', state);

  const response = await fetch(callbackUrl.toString(), {
    method: 'GET',
    headers: authHeaders,
    redirect: 'manual',
  });

  if (response.status >= 400) {
    throw new Error('Failed to complete MCP OAuth authorization');
  }
};

const runOAuthAuthorization = async ({
  authorizeUrl,
}: {
  authorizeUrl: string;
}): Promise<void> => {
  if (window.electronAPI?.shell?.openExternal && window.electronAPI.mcpOAuth) {
    const cancelAuthorizationWaitRef: { current: (() => void) | null } = {
      current: null,
    };
    const authorizationResult = new Promise<{
      code?: string;
      state?: string;
    }>((resolve, reject) => {
      let unsubscribeSuccess: (() => void) | null = null;
      let unsubscribeError: (() => void) | null = null;
      let timeout: number | null = null;

      const finish = () => {
        if (timeout !== null) window.clearTimeout(timeout);
        unsubscribeSuccess?.();
        unsubscribeError?.();
      };
      cancelAuthorizationWaitRef.current = finish;
      timeout = window.setTimeout(() => {
        finish();
        reject(new Error('MCP OAuth authorization timed out'));
      }, 120_000);
      unsubscribeSuccess = window.electronAPI.mcpOAuth.onSuccess((payload) => {
        finish();
        resolve(payload);
      });
      unsubscribeError = window.electronAPI.mcpOAuth.onError(({ error }) => {
        finish();
        reject(new Error(error || 'MCP OAuth authorization failed'));
      });
    });

    const result = await window.electronAPI.shell.openExternal(authorizeUrl);
    if (!result.success) {
      cancelAuthorizationWaitRef.current?.();
      throw new Error(result.error ?? 'MCP OAuth authorization failed');
    }
    const callbackPayload = await authorizationResult;
    if (callbackPayload.code && callbackPayload.state) {
      await completeOAuthCallback({
        code: callbackPayload.code,
        state: callbackPayload.state,
      });
    }
    return;
  }

  const popup = window.open(
    authorizeUrl,
    'mcp-oauth',
    'width=600,height=700,scrollbars=yes',
  );
  if (!popup) {
    throw new Error('OAuth popup was blocked');
  }

  const callbackPayload = await waitForMcpOAuthPopupCallback({
    popup,
    authorizationFailedMessage: 'MCP OAuth authorization failed',
    authorizationTimedOutMessage: 'MCP OAuth authorization timed out',
    authorizationWindowClosedMessage: 'MCP OAuth authorization window closed',
  });
  if (callbackPayload.code && callbackPayload.state) {
    await completeOAuthCallback({
      code: callbackPayload.code,
      state: callbackPayload.state,
    });
  }
};

const isMissingMcpAuthIdentityError = (message: string): boolean =>
  message.includes('has no auth identity') ||
  message.includes('has no OAuth config');

const ensureDefaultOAuthDcrIdentity = async ({
  mcpServerId,
  discoveryUrl,
}: {
  mcpServerId: string;
  discoveryUrl: string;
}): Promise<void> => {
  const authHeaders = await getAuthHeaders();
  const response = await mcpServersClient.upsertMcpCredential({
    params: { id: mcpServerId },
    body: {
      type: 'OAUTH_DCR',
      discoveryUrl: discoveryUrl.trim(),
      scopes: [],
      label: 'Default',
    },
    overrideClientOptions: buildClientOptions(),
    extraHeaders: authHeaders,
  });
  if (response.status !== 200) {
    throw new Error(
      getMcpApiErrorMessage(
        response.body,
        'Failed to create MCP OAuth identity',
      ),
    );
  }
};

const resolveAuthorizeUrl = async ({
  mcpServerId,
  identityId,
  discoveryUrl,
}: {
  mcpServerId: string;
  identityId?: string;
  discoveryUrl?: string | null;
}): Promise<string> => {
  try {
    return await fetchAuthorizeUrl({ mcpServerId, identityId });
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Failed to authorize MCP server';
    if (
      identityId ||
      !isMissingMcpAuthIdentityError(errorMessage) ||
      !discoveryUrl?.trim()
    ) {
      throw error;
    }
    await ensureDefaultOAuthDcrIdentity({
      mcpServerId,
      discoveryUrl,
    });
    return fetchAuthorizeUrl({ mcpServerId });
  }
};

export const mcpOAuthAuthorizeHandler: PortalToolHandler = async ({
  input,
  toolCallId,
  batchId,
  applicationId,
}) => {
  const parsedInput = parsePortalToolHandlerInput({
    schema: McpOAuthAuthorizeInputSchema,
    input,
    toolName: MCP_OAUTH_AUTHORIZE_TOOL_NAME,
    toolCallId,
    batchId,
  });
  if (!parsedInput.ok) {
    return parsedInput.result;
  }

  if (!isElectronApiAvailable()) {
    return getElectronApiUnavailableResult({
      toolName: MCP_OAUTH_AUTHORIZE_TOOL_NAME,
      toolCallId,
      batchId,
    });
  }

  try {
    const aiApplication =
      await terraProxyApiClient.getAiApplication(applicationId);
    const mcpServerId = aiApplication.mcpServerId;
    if (!mcpServerId) {
      return {
        status: 'error',
        result: McpOAuthAuthorizeOutputSchema.parse({
          message: 'No MCP server is linked to this AI application',
        }),
      };
    }

    const identityId = parsedInput.data.identityId?.trim() || undefined;

    const authorizeUrl = await resolveAuthorizeUrl({
      mcpServerId,
      identityId,
      discoveryUrl: parsedInput.data.discoveryUrl?.trim() || aiApplication.url,
    });

    await runOAuthAuthorization({ authorizeUrl });

    return {
      status: 'success',
      result: McpOAuthAuthorizeOutputSchema.parse({
        message: 'MCP OAuth authorization completed successfully',
        data: {
          mcpServerId,
          identityId,
        },
      }),
    };
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'MCP OAuth authorization failed';

    if (isMissingMcpAuthIdentityError(errorMessage)) {
      openMcpConnectionSettings({
        applicationId,
        identityId: parsedInput.data.identityId,
        oauthOnly: true,
      });
      return {
        status: 'success',
        result: McpOAuthAuthorizeOutputSchema.parse({
          message:
            'No OAuth identity is configured yet. Opened MCP connection settings — configure and save OAuth credentials, then retry authorization.',
          data: { openedSettings: true },
        }),
      };
    }

    return {
      status: 'error',
      result: McpOAuthAuthorizeOutputSchema.parse({
        message: errorMessage,
      }),
    };
  }
};
